import React, { useState, useEffect, useMemo } from 'react';
import { getProfitSummary, ProfitMobileSummary, getHourlyProfit, HourlyProfitBreakdown, getDailyProfit, getDailyProfitRange, DailyProfitSummary } from '../api/profit';
import { TrendingUpIcon, DollarIcon, BarChart2Icon, StopwatchIcon } from './shared/Icons';
import SectionTitle from './shared/SectionTitle';
import StandaloneSection from './shared/StandaloneSection';
import DateRangeSelector from './shared/DateRangeSelector';
import ComparisonTable from './shared/ComparisonTable';
import AdaptiveProfitChartSection from './shared/AdaptiveProfitChartSection';
import DimensionalBreakdowns from './shared/DimensionalBreakdowns';
import ByAssetTab from './shared/ByAssetTab';
import ByTimeTab from './shared/ByTimeTab';
import ByPerformanceTab from './shared/ByPerformanceTab';
import ExportsTab from './shared/ExportsTab';
import IntelligentInsights from './shared/IntelligentInsights';
import { useGlobalAutoRefreshStore, useAutoRefreshTrigger } from '../stores/useGlobalAutoRefreshStore';
import { useDateRange, useDateRangeChangeCounter, useDateRangeStore } from '../stores/useDateRangeStore';
import { useAdaptiveChartData } from '../hooks/useAdaptiveChartData';
import { useBreakdownData } from '../hooks/useBreakdownData';
import { useComparison } from '../stores/useComparisonStore';
import { formatDateRangeDisplay } from '../utils/dateUtils';
import { getChartTitle, calculateDaysBetween } from '../utils/chartUtils';
import { InsightGenerationData } from '../utils/insightOrchestration';
import './DailyProfitMonitor.css';
import './shared/MetricCards.css';

interface DailyProfitMonitorProps {
  className?: string;
}

const DailyProfitMonitor: React.FC<DailyProfitMonitorProps> = ({ className = '' }) => {
  const setActiveDashboard = useGlobalAutoRefreshStore((state) => state.setActiveDashboard);
  const refreshTrigger = useAutoRefreshTrigger('profit');
  
  // Use date range from store
  const { rangeType, dateRange } = useDateRange();
  const rangeChangeCounter = useDateRangeChangeCounter();
  const { setCustomRange } = useDateRangeStore();

  // Get comparison data for win rate trend analysis
  const comparison = useComparison();

  // Use adaptive chart data hook
  const chartData = useAdaptiveChartData(dateRange, refreshTrigger);
  
  // Use breakdown data hook
  const breakdownData = useBreakdownData(dateRange);
  
  // Calculate days difference for chart title
  const daysDiff = calculateDaysBetween(dateRange.start_date, dateRange.end_date);
  const chartTitle = getChartTitle(chartData.granularity, daysDiff);

  const [summary, setSummary] = useState<ProfitMobileSummary | null>(null);
  const [hourlyData, setHourlyData] = useState<HourlyProfitBreakdown[]>([]);
  const [dailyTotal, setDailyTotal] = useState<DailyProfitSummary | null>(null);
  const [dailyBreakdown, setDailyBreakdown] = useState<DailyProfitSummary[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingHourly, setLoadingHourly] = useState(true);
  const [loadingDaily, setLoadingDaily] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAssetBreakdown, setShowAssetBreakdown] = useState(true);

  // Determine if we're showing a single day (for hourly breakdown)
  const isSingleDay = dateRange.start_date === dateRange.end_date;

  // Set this dashboard as active when mounted, clear when unmounted
  useEffect(() => {
    setActiveDashboard('profit');
    return () => setActiveDashboard(null);
  }, [setActiveDashboard]);

  // When global refresh trigger changes, reload data
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadSummaryData();
      loadProfitData();
    }
  }, [refreshTrigger]);

  const loadProfitData = async () => {
    try {
      setError(null);

      // For single day, load hourly data
      if (isSingleDay) {
        setLoadingHourly(true);
        const hourly = await getHourlyProfit(dateRange.start_date, undefined);

        // Create a complete 24-hour array with zeros for missing hours
        const completeHourlyData: HourlyProfitBreakdown[] = [];
        for (let hour = 0; hour < 24; hour++) {
          const existingHour = hourly.find(h => h.trade_hour === hour);
          if (existingHour) {
            completeHourlyData.push(existingHour);
          } else {
            completeHourlyData.push({
              trade_date: dateRange.start_date,
              trade_hour: hour,
              product_id: undefined,
              gross_profit: 0,
              net_profit: 0,
              trade_count: 0
            });
          }
        }
        setHourlyData(completeHourlyData);
        setLoadingHourly(false);
      } else {
        // For date ranges, clear hourly data
        setHourlyData([]);
        setLoadingHourly(false);
      }

      // Load daily data based on whether it's a single day or range
      setLoadingDaily(true);

      if (isSingleDay) {
        // For single day, load breakdown by asset
        const dailyAll = await getDailyProfit(dateRange.start_date, undefined);

        if (Array.isArray(dailyAll)) {
          // Store the full breakdown for display (all assets)
          setDailyBreakdown(dailyAll);

          // Sum all products for total
          const total = dailyAll.reduce((sum, item) => ({
            ...item,
            gross_profit: sum.gross_profit + item.gross_profit,
            net_profit: sum.net_profit + item.net_profit,
            trade_count: sum.trade_count + item.trade_count,
            fees_paid: sum.fees_paid + item.fees_paid
          }), {
            trade_date: dateRange.start_date,
            product_id: undefined,
            gross_profit: 0,
            net_profit: 0,
            trade_count: 0,
            fees_paid: 0
          });
          setDailyTotal(total);
        } else {
          setDailyBreakdown([]);
          setDailyTotal(dailyAll);
        }
      } else {
        // For date range, fetch all daily data and aggregate
        const dailyRangeData = await getDailyProfitRange(dateRange.start_date, dateRange.end_date);

        // Aggregate totals across all days in the range
        const total = dailyRangeData.reduce((sum, day) => ({
          trade_date: dateRange.end_date, // Use end date for display
          product_id: undefined,
          gross_profit: sum.gross_profit + day.gross_profit,
          net_profit: sum.net_profit + day.net_profit,
          trade_count: sum.trade_count + day.trade_count,
          fees_paid: sum.fees_paid + day.fees_paid
        }), {
          trade_date: dateRange.end_date,
          product_id: undefined,
          gross_profit: 0,
          net_profit: 0,
          trade_count: 0,
          fees_paid: 0
        });

        setDailyTotal(total);
        setDailyBreakdown([]); // No per-asset breakdown for ranges
      }

      setLoadingDaily(false);

    } catch (err) {
      console.error('Error loading profit data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profit data');
      setLoadingHourly(false);
      setLoadingDaily(false);
    }
  };

  const loadSummaryData = async () => {
    try {
      setLoadingSummary(true);
      const summaryData = await getProfitSummary(dateRange.start_date, undefined);
      setSummary(summaryData);
    } catch (err) {
      console.error('Error loading summary data:', err);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleDayClick = async (date: string) => {
    // When clicking a specific day in the 7-day trend, update the store to show that day
    setCustomRange({
      start_date: date,
      end_date: date
    });
  };

  const formatDateForTitle = (currentRangeType: string, range: { start_date: string; end_date: string }) => {
    const displayStr = formatDateRangeDisplay(range, currentRangeType as 'today' | 'yesterday' | 'last7days' | 'last30days' | 'mtd' | 'lastmonth' | 'qtd' | 'ytd' | 'custom');
    
    // For single day ranges, add "Profit" suffix
    if (range.start_date === range.end_date) {
      return `${displayStr} Profit`.toUpperCase();
    }
    
    // For multi-day ranges
    return `${displayStr} - Profit Summary`.toUpperCase();
  };

  // Load summary data once on mount, then load profit data
  useEffect(() => {
    const loadInitialData = async () => {
      await loadSummaryData();
      await loadProfitData();
    };

    loadInitialData();
  }, []); // Empty dependency array - only run once on mount

  // Reload summary and profit data when date range changes
  useEffect(() => {
    // Skip on initial mount
    if (rangeChangeCounter === 0) return;

    const loadData = async () => {
      await loadSummaryData();
      await loadProfitData();
    };

    loadData();
  }, [rangeChangeCounter]); // Trigger when range changes

  // Prepare data for insights generation
  const insightData: InsightGenerationData = useMemo(() => {
    // Use day of week breakdowns to get daily data, as chartData may be aggregated to weekly/monthly
    // for large date ranges. Breakdown data always maintains daily granularity.
    const dailyData = breakdownData.data.dayOfWeekBreakdowns.length > 0
      ? breakdownData.data.dayOfWeekBreakdowns.flatMap(dow =>
          // Each day of week breakdown represents aggregated data across all occurrences of that day
          // We need to convert this back to individual day records for insight detection
          // For now, we'll use the aggregated data as representative daily data
          [{
            trade_date: dateRange.start_date, // Using start date as placeholder
            product_id: undefined,
            gross_profit: dow.gross_profit / (dow.occurrences || 1), // Average per occurrence
            net_profit: dow.net_profit / (dow.occurrences || 1),
            trade_count: Math.round(dow.trade_count / (dow.occurrences || 1)),
            fees_paid: dow.fees_paid / (dow.occurrences || 1),
          }]
        ).filter(d => d.trade_count > 0) // Only include days with actual trading
      : chartData.data
          .filter(d => d.period_type === 'day') // Fallback to chart data if available
          .map(d => ({
            trade_date: d.timestamp.substring(0, 10),
            product_id: undefined,
            gross_profit: d.gross_profit,
            net_profit: d.net_profit,
            trade_count: d.trade_count,
            fees_paid: d.fees_paid || 0,
          }));

    // Convert breakdown hour data to the format expected by insight detectors
    // Each breakdown item already represents aggregated data for one hour across all days
    const hourlyData = breakdownData.data.hourOfDayBreakdowns.length > 0
      ? breakdownData.data.hourOfDayBreakdowns.map(h => ({
          trade_date: dateRange.start_date, // Aggregated across date range
          trade_hour: h.hour,
          gross_profit: h.gross_profit,
          net_profit: h.net_profit,
          trade_count: h.trade_count,
        }))
      : undefined;

    return {
      dailyData,
      hourlyData,
      assetBreakdowns: breakdownData.data.assetBreakdowns,
      previousPeriodData: comparison.data || undefined,
      dateRange,
    };
  }, [chartData.data, breakdownData.data, comparison.data, dateRange]);


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (value: number): string => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const getProfitChangeClass = (change: number) => {
    if (change > 0) return 'profit-positive';
    if (change < 0) return 'profit-negative';
    return 'profit-neutral';
  };

  const formatTime = (hour: number) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}${period}`;
  };

  const getPeakHours = () => {
    if (hourlyData.length === 0) return [];
    return [...hourlyData]
      .filter(h => h.net_profit > 0)
      .sort((a, b) => b.net_profit - a.net_profit)
      .slice(0, 3);
  };

  const formatDateShort = (dateStr: string) => {
    const parts = dateStr.split('-');
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1;
    const day = parseInt(parts[2]);
    const date = new Date(Date.UTC(year, month, day));

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC'
    });
  };

  const maxProfit = Math.max(...hourlyData.map(h => h.net_profit), 0.01);
  const maxTrendProfit = summary?.seven_day_trend
    ? Math.max(...summary.seven_day_trend.map(d => d.net_profit), 0.01)
    : 0.01;

  if (error) {
    return (
      <div className={`daily-profit-monitor ${className}`}>
        <div className="profit-error">
          <h3>Failed to Load Profit Data</h3>
          <p>{error}</p>
          <button onClick={() => { loadSummaryData(); loadProfitData(); }} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`daily-profit-monitor ${className}`}>
      {/* Dashboard Header with Title and Date Range Selector */}
      <div className="dashboard-header">
        <SectionTitle title="Profit Monitor" />
        <div className="header-controls">
          <DateRangeSelector />
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-content">
        {/* Hero Metrics Section */}
          <StandaloneSection title={formatDateForTitle(rangeType, dateRange)}>
            <div className="hero-metrics">
              {/* Primary Hero Metric - Net Profit */}
              <div className="hero-metric hero-metric-primary">
                <div className="hero-label">Net Profit</div>
                <div className={`hero-value-primary ${getProfitChangeClass(dailyTotal?.net_profit || 0)}`}>
                  {loadingDaily ? (
                    <div className="loading-spinner-inline"></div>
                  ) : (
                    formatCurrency(dailyTotal?.net_profit || 0)
                  )}
                </div>
                {summary && summary.change_amount !== undefined && summary.change_amount !== 0 && isSingleDay && (
                  <div className={`hero-change ${getProfitChangeClass(summary.change_amount)}`}>
                    <span className="change-icon">{summary.change_amount > 0 ? '↑' : '↓'}</span>
                    {formatCurrency(Math.abs(summary.change_amount))} ({formatPercentage(summary.change_percentage)})
                  </div>
                )}
              </div>

              {/* Supporting Metrics */}
              <div className="hero-metric">
                <div className="hero-label">Gross Profit</div>
                <div className={`hero-value ${getProfitChangeClass(dailyTotal?.gross_profit || 0)}`}>
                  {loadingDaily ? (
                    <div className="loading-spinner-inline"></div>
                  ) : (
                    formatCurrency(dailyTotal?.gross_profit || 0)
                  )}
                </div>
              </div>

              <div className="hero-metric">
                <div className="hero-label">Total Trades</div>
                <div className="hero-value">
                  {loadingDaily ? (
                    <div className="loading-spinner-inline"></div>
                  ) : (
                    dailyTotal?.trade_count || 0
                  )}
                </div>
                {dailyTotal && dailyTotal.trade_count > 0 && dailyTotal.net_profit !== 0 && (
                  <div className="hero-change">
                    Avg: {formatCurrency(dailyTotal.net_profit / dailyTotal.trade_count)}
                  </div>
                )}
              </div>

              <div className="hero-metric">
                <div className="hero-label">Fees Paid</div>
                <div className="hero-value negative">
                  {loadingDaily ? (
                    <div className="loading-spinner-inline"></div>
                  ) : (
                    formatCurrency(dailyTotal?.fees_paid || 0)
                  )}
                </div>
              </div>
            </div>
          </StandaloneSection>

        {/* Profit Trend Section */}
          <StandaloneSection title="PROFIT TREND">
            <AdaptiveProfitChartSection
              title={chartTitle}
              data={chartData.data}
              granularity={chartData.granularity}
              chartType={chartData.chartType}
              loading={chartData.loading}
              error={chartData.error}
              onDataPointClick={(dataPoint) => {
                // Drill down: update date range to show that specific period
                if ('period_start' in dataPoint) {
                  // For aggregated data (weekly/monthly), drill down to that period
                  setCustomRange({
                    start_date: dataPoint.period_start,
                    end_date: dataPoint.period_end
                  });
                } else if (dataPoint.period_type === 'day') {
                  // For daily data, drill down to that day's hourly view
                  const date = dataPoint.timestamp.substring(0, 10);
                  setCustomRange({
                    start_date: date,
                    end_date: date
                  });
                }
                // For hourly data, no drill-down (already at finest granularity)
              }}
            />
          </StandaloneSection>

        {/* Comparative Analysis Section */}
        <ComparisonTable currentData={dailyBreakdown.length > 0 ? dailyBreakdown : (dailyTotal ? [dailyTotal] : [])} />

        {/* Intelligent Insights Section */}
        <IntelligentInsights
          data={insightData}
          isLoading={breakdownData.isLoading || chartData.loading}
          error={breakdownData.error || chartData.error}
        />

        {/* Breakdown Analysis Section */}
          <StandaloneSection title="BREAKDOWN ANALYSIS">
            <DimensionalBreakdowns>
              {(activeTab) => {
                if (activeTab === 'asset') {
                  return (
                    <ByAssetTab
                      breakdowns={breakdownData.data.assetBreakdowns}
                      isLoading={breakdownData.isLoading}
                      error={breakdownData.error}
                      onRetry={() => {
                        // Trigger refetch by incrementing refresh counter
                        window.location.reload();
                      }}
                    />
                  );
                } else if (activeTab === 'time') {
                  return (
                    <ByTimeTab
                      dayOfWeekBreakdowns={breakdownData.data.dayOfWeekBreakdowns}
                      hourOfDayBreakdowns={breakdownData.data.hourOfDayBreakdowns}
                      isLoading={breakdownData.isLoading}
                      error={breakdownData.error}
                      onRetry={() => {
                        window.location.reload();
                      }}
                    />
                  );
                } else if (activeTab === 'performance') {
                  return (
                    <ByPerformanceTab
                      profitabilityMetrics={breakdownData.data.profitabilityMetrics}
                      efficiencyMetrics={breakdownData.data.efficiencyMetrics}
                      isLoading={breakdownData.isLoading}
                      error={breakdownData.error}
                      onRetry={() => {
                        window.location.reload();
                      }}
                    />
                  );
                } else if (activeTab === 'exports') {
                  return (
                    <ExportsTab
                      isLoading={breakdownData.isLoading}
                      error={breakdownData.error}
                      onRetry={() => {
                        window.location.reload();
                      }}
                    />
                  );
                }
                return null;
              }}
            </DimensionalBreakdowns>
          </StandaloneSection>
       </div>
     </div>
   );
 };

 export default DailyProfitMonitor;
